import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useWindowVirtualizer } from "@tanstack/react-virtual";

/**
 * Render only the rows on screen, whatever the list has grown to.
 *
 * A windowed fetch bounds what is *loaded*; this bounds what is *in the DOM*. They solve different
 * halves of the same problem and neither replaces the other: somebody who keeps scrolling a
 * windowed list still accumulates every row they have passed, and eighty thousand table rows is a
 * slow page whether or not the fetching was polite about it.
 *
 * Measured rather than fixed-height. These rows are not uniform — an organization name wraps to two
 * lines, a formatted date to three — and a virtualizer told to assume one height leaves gaps and
 * jitter as the estimate drifts from reality. Each mounted row reports its real height back.
 *
 * Measured against the *window*, not an inner scroller. The admin shell scrolls the document, so
 * an inner scroll box would trap the wheel inside the table and strand the page header above it.
 */

/**
 * Starting guess for a row's height, in pixels, before it has been measured.
 *
 * Only affects the scrollbar on first paint — every mounted row is measured and the estimate is
 * corrected. Taken from the Users table's two-line rows.
 */
const ESTIMATED_ROW_HEIGHT = 72;

/**
 * Rows rendered beyond the viewport on each side.
 *
 * Enough that a fast scroll does not reach empty space before React has caught up, few enough that
 * the DOM stays small. Also what keeps in-page find usable for a screen or two either side.
 */
const OVERSCAN = 12;

export const useVirtualRows = <TElement extends HTMLElement = HTMLDivElement>(
    count: number,
    estimateHeight = ESTIMATED_ROW_HEIGHT,
) => {
    /** Wraps the list; where it sits in the document is the virtualizer's origin. */
    const listRef = useRef<TElement | null>(null);

    /*
     * How far down the document the list starts.
     *
     * `offsetTop` would be the obvious answer and is the wrong one here: it is measured from the
     * nearest *positioned* ancestor, and the admin shell's content pane is `relative`. Every row
     * would then be placed short by the height of the page header, which reads as the list
     * scrolling out of step with the page rather than as an arithmetic error.
     *
     * `getBoundingClientRect().top + scrollY` is the document position regardless of what is
     * positioned above it.
     */
    const [
        listStart,
        setListStart,
    ] = useState(0);

    const measureStart = useCallback(() => {
        const el = listRef.current;
        if (!el) return;
        const next = Math.round(el.getBoundingClientRect().top + window.scrollY);
        // Only on a real change: this runs on every resize, and setting identical state would
        // re-render the whole table for nothing.
        setListStart((prev) => (prev === next ? prev : next));
    }, []);

    // Layout effect, not effect: the position has to be known before the browser paints, or the
    // first frame renders the slice at the wrong offset and visibly jumps.
    useLayoutEffect(() => {
        measureStart();
        if (typeof window === "undefined") return;
        window.addEventListener("resize", measureStart);
        return () => window.removeEventListener("resize", measureStart);
    }, [
        measureStart,
        count,
    ]);

    const virtualizer = useWindowVirtualizer({
        count,
        estimateSize: () => estimateHeight,
        overscan: OVERSCAN,
        scrollMargin: listStart,
    });

    const items = virtualizer.getVirtualItems();

    return {
        listRef,
        items,
        /** Total height of the full list, so the scrollbar is the size it would have been. */
        totalHeight: virtualizer.getTotalSize(),
        /** Attach to each rendered row so its real height is fed back. */
        measureElement: virtualizer.measureElement,
        /**
         * Where the rendered slice begins, relative to the list itself.
         *
         * The virtualizer reports positions in document space; subtracting the list's own start
         * turns that into the padding a spacer row needs.
         */
        offsetTop: (items[0]?.start ?? 0) - listStart,
        /** Space left below the slice, so the list keeps its full height. */
        offsetBottom: virtualizer.getTotalSize() - ((items[items.length - 1]?.end ?? 0) - listStart),
    };
};
